<!-- Conversation Viewer Page -->
<div class="h-full p-8">
  <!-- Header -->
  <div class="mb-5 flex items-center gap-4">
    <!-- On mobile, hide back button -->
    <div class="hidden md:block">
      <button mat-icon-button (click)="goBack()" class="!-ml-2">
        <lucide-angular [img]="ArrowLeftIcon" class="w-5 h-5"></lucide-angular>
      </button>
    </div>
    <div>
      <div class="flex flex-col md:flex-row">
        <h1 class="!mb-0">
          Visualizador de Conversaciones
        </h1>
        @if (!isLoadingParams()) {
          <div class="hidden md:flex mt-1 mx-4 text-light-text-tertiary dark:text-dark-text-tertiary">
            <lucide-angular [img]="ChevronRightIcon" class="w-5 h-5"></lucide-angular>
          </div>
        }
        @if (!isLoadingParams()) {
          <h2 class=" !font-normal pt-0.5">
            {{ currentChatbot()?.name || '' }}
          </h2>
        }
      </div>
      <p class="normal !mb-0">
        Busca conversaciones por fecha o ID
      </p>
    </div>
  </div>

  <!-- Desktop Search Section -->
  <div class="hidden md:block mb-3 bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-2xl p-4">
    <div class="flex flex-wrap items-center gap-2 justify-between">
      @if (isLoadingParams()) {
        <div class="flex items-center gap-2 justify-center w-full py-3.5">
          <mat-spinner diameter="24"></mat-spinner>
          <span class="text-light-text-secondary dark:text-dark-text-secondary">
            Cargando parámetros de búsqueda...
          </span>
        </div>
      }
      @if (!isLoadingParams()) {
        <div  class="flex flex-wrap items-center gap-4 justify-between">
          <!-- Dynamic Search Parameter Selection (incluye Session ID) -->
          <mat-form-field appearance="fill" subscriptSizing="dynamic">
            <mat-label>Buscar por</mat-label>
            <mat-select [value]="selectedParam()" (selectionChange)="selectedParam.set($event.value); paramValue.set(''); searchId = ''; searchDate = null">
              @for (param of availableParams(); track param) {
                <mat-option [value]="param.queryParam">
                  {{ param.label }}
                </mat-option>
              }
              <mat-option value="sessionId">Session ID</mat-option>
            </mat-select>
          </mat-form-field>
          <!-- Dynamic Input based on param type -->
          @switch (selectedParam()) {
            <!-- Session ID input -->
            @case ('sessionId') {
              <mat-form-field class="w-80" appearance="fill" subscriptSizing="dynamic">
                <mat-label>Session ID</mat-label>
                <input matInput [(ngModel)]="searchId" placeholder="ej: c0fe8b21-dbf9-40dd-8052-0343ed6a651b" (keydown.enter)="search()">
              </mat-form-field>
            }
            <!-- Date input -->
            @case (currentParamInfo()?.dataType === 'datetime' ? selectedParam() : null) {
              <mat-form-field appearance="fill" subscriptSizing="dynamic">
                <mat-label>{{ currentParamInfo()?.label }}</mat-label>
                <input matInput [matDatepicker]="picker" [(ngModel)]="searchDate" (dateChange)="onDateChange($event)">
                <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                <mat-datepicker #picker></mat-datepicker>
              </mat-form-field>
            }
            <!-- Number input -->
            @case (currentParamInfo()?.dataType === 'number' ? selectedParam() : null) {
              <mat-form-field appearance="fill" subscriptSizing="dynamic" class="w-80">
                <mat-label>{{ currentParamInfo()?.label }}</mat-label>
                <input matInput type="number" [(ngModel)]="paramValue" (keydown.enter)="search()">
              </mat-form-field>
            }
            <!-- Boolean input -->
            @case (currentParamInfo()?.dataType === 'boolean' ? selectedParam() : null) {
              <mat-form-field appearance="fill" subscriptSizing="dynamic">
                <mat-label>{{ currentParamInfo()?.label }}</mat-label>
                <mat-select [(ngModel)]="paramValue">
                  <mat-option value="true">Sí</mat-option>
                  <mat-option value="false">No</mat-option>
                </mat-select>
              </mat-form-field>
            }
            <!-- String input (default) -->
            @default {
              <mat-form-field appearance="fill" subscriptSizing="dynamic" class="w-80">
                <mat-label>{{ currentParamInfo()?.label }}</mat-label>
                <input matInput [(ngModel)]="paramValue" (keydown.enter)="search()" (input)="onParamValueInput()">
              </mat-form-field>
            }
          }
        </div>
      }

      <!-- Search Button -->
      @if (!isLoadingParams()) {
        <button (click)="search()" [disabled]="isLoading() || (!searchId.trim() && !searchDate && !paramValue().trim())"
          class="h-12 px-8 bg-light-primary dark:bg-dark-primary text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
          <lucide-icon [name]="SearchIcon" class="w-5 h-5"></lucide-icon>
          Buscar
        </button>
      }
    </div>
  </div>

  <!-- Mobile Search Section -->
  <div class="md:hidden mb-3">
    @if (isLoadingParams()) {
      <div class="flex items-center gap-2 justify-center w-full py-3.5">
        <mat-spinner diameter="24"></mat-spinner>
        <span class="text-light-text-secondary dark:text-dark-text-secondary">
          Cargando parámetros de búsqueda...
        </span>
      </div>
    }

    @if (!isLoadingParams()) {
      <div class="flex flex-col gap-4">
        <div class="flex flex-row gap-2">
          <!-- Dynamic Search Parameter Selection (incluye Session ID) -->
          <mat-form-field class="mini-form-field !min-w-[120px]" appearance="fill" subscriptSizing="dynamic">
            <mat-label>Buscar por</mat-label>
            <mat-select [value]="selectedParam()" (selectionChange)="selectedParam.set($event.value); paramValue.set(''); searchId = ''; searchDate = null">
              @for (param of availableParams(); track param) {
                <mat-option [value]="param.queryParam">
                  {{ param.label }}
                </mat-option>
              }
              <mat-option value="sessionId">Session ID</mat-option>
            </mat-select>
          </mat-form-field>
          <!-- Dynamic Input based on param type (siempre visible) -->
          @switch (selectedParam()) {
            <!-- Session ID input -->
            @case ('sessionId') {
              <mat-form-field class="mini-form-field" appearance="fill" subscriptSizing="dynamic">
                <mat-label>Session ID</mat-label>
                <input matInput [(ngModel)]="searchId" placeholder="ej: c0fe8b21-dbf9-40dd-8052-0343ed6a651b" (keydown.enter)="search()">
              </mat-form-field>
            }
            <!-- Date input -->
            @case (currentParamInfo()?.dataType === 'datetime' ? selectedParam() : null) {
              <mat-form-field class="mini-form-field" appearance="fill" subscriptSizing="dynamic">
                <mat-label>{{ currentParamInfo()?.label }}</mat-label>
                <input matInput [matDatepicker]="pickerMobile" [(ngModel)]="searchDate" (dateChange)="onDateChange($event)">
                <mat-datepicker-toggle matIconSuffix [for]="pickerMobile"></mat-datepicker-toggle>
                <mat-datepicker #pickerMobile></mat-datepicker>
              </mat-form-field>
            }
            <!-- String input (default) -->
            @default {
              <mat-form-field class="mini-form-field" appearance="fill" subscriptSizing="dynamic">
                <mat-label>{{ currentParamInfo()?.label }}</mat-label>
                <input matInput [(ngModel)]="paramValue" (keydown.enter)="search()">
              </mat-form-field>
            }
            <!-- Number input -->
            @case (currentParamInfo()?.dataType === 'number' ? selectedParam() : null) {
              <mat-form-field class="mini-form-field" appearance="fill" subscriptSizing="dynamic">
                <mat-label>{{ currentParamInfo()?.label }}</mat-label>
                <input matInput type="number" [(ngModel)]="paramValue" (keydown.enter)="search()">
              </mat-form-field>
            }
            <!-- Boolean input -->
            @case (currentParamInfo()?.dataType === 'boolean' ? selectedParam() : null) {
              <mat-form-field class="mini-form-field" appearance="fill" subscriptSizing="dynamic" class="w-full">
                <mat-label>{{ currentParamInfo()?.label }}</mat-label>
                <mat-select [(ngModel)]="paramValue">
                  <mat-option value="true">Sí</mat-option>
                  <mat-option value="false">No</mat-option>
                </mat-select>
              </mat-form-field>
            }
          }
          <!-- Search Button -->
          <button (click)="search()" [disabled]="isLoading() || (!searchId.trim() && !searchDate && !paramValue().trim())"
            class="h-12 px-4 self-start bg-light-primary dark:bg-dark-primary text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            <lucide-icon [name]="SearchIcon" class="w-5 h-5"></lucide-icon>
          </button>
        </div>
      </div>
    }
  </div>

  <!-- Loading Spinner -->
  @if (isLoading()) {
    <div class="flex flex-col items-center justify-center py-16">
      <mat-spinner diameter="50"></mat-spinner>
      <p class="pt-4 normal">
        Cargando conversación...
      </p>
    </div>
  }

  <!-- Results Section -->
  @if (sessions().length > 0) {
    <div class="space-y-3 pb-3 dark:[color-scheme:dark]">
      <!-- Sessions Table -->
      <app-data-table
        [data]="sessions()"
        [columns]="sessionColumns"
        [actions]="sessionActions"
        [config]="tableConfig"
        [filterFunction]="filterSessions"
        [loadingMore]="isLoadingMore()"
        [hasMore]="hasMoreSessions()"
        (loadMore)="loadMoreSessions()"
        (rowClick)="onSessionRowClick($event)">
        <ng-template #customFilters>
          <!-- Botón solo visible en mobile -->
          <button
            class="md:hidden h-10 px-4 mb-2 bg-light-primary dark:bg-dark-primary text-white rounded-xl font-medium hover:opacity-90 transition-opacity"
            (click)="toggleMobileFilters()"
            type="button">
            {{ showMobileFilters ? 'Ocultar filtros' : 'Mostrar filtros' }}
          </button>
          <!-- Desktop filters -->
          <div class="hidden md:flex items-center gap-4 mb-4">
            <mat-form-field appearance="fill" subscriptSizing="dynamic">
              <mat-label>Fuente</mat-label>
              <mat-select [value]="sourceFilter()" (selectionChange)="sourceFilter.set($event.value)">
                <mat-option value="all">Todas</mat-option>
                <mat-option value="customFront">customFront</mat-option>
                <mat-option value="whatsapp">whatsapp</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="fill" subscriptSizing="dynamic">
              <mat-label>Teléfono</mat-label>
              <input matInput [value]="phoneFilter()" (input)="phoneFilter.set($any($event.target).value)" placeholder="Filtrar por teléfono">
              <lucide-angular matPrefix [img]="PhoneIcon" class="w-4 h-4"></lucide-angular>
            </mat-form-field>
          </div>
          <!-- Mobile Filters -->
          @if (showMobileFilters) {
            <div class="md:hidden mb-2 flex flex-row gap-4 w-full">
              <mat-form-field class="mini-form-field" appearance="fill" subscriptSizing="dynamic">
                <mat-label>Fuente</mat-label>
                <mat-select [value]="sourceFilter()" (selectionChange)="sourceFilter.set($event.value)">
                  <mat-option value="all">Todas</mat-option>
                  <mat-option value="customFront">customFront</mat-option>
                  <mat-option value="whatsapp">whatsapp</mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field class="mini-form-field" appearance="fill" subscriptSizing="dynamic">
                <mat-label>Teléfono</mat-label>
                <input matInput [value]="phoneFilter()" (input)="phoneFilter.set($any($event.target).value)" placeholder="Filtrar por teléfono">
                <lucide-angular matPrefix [img]="PhoneIcon" class="w-4 h-4"></lucide-angular>
              </mat-form-field>
            </div>
          }
        </ng-template>
      </app-data-table>
    </div>
  }

  <!-- Empty State -->
  @if (!isLoading() && sessions().length === 0) {
    <app-empty-state
      [icon]="SearchIcon"
      title="Busca una conversación"
      message="Ingresa un Conversation ID o selecciona una fecha para visualizar las sesiones"
    ></app-empty-state>
  }
</div>
